import { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import {
  fetchSectionById,
  deleteSection,
  addItemToSection,
  removeItemFromSection,
  updateSectionItemPositions,
  addBannerToSection,
  deleteBanner,
  updateBanner,
} from "@/api/sections";
import { supabase } from "@/integrations/supabase/client";
import AdminLayout from "@/components/layout/AdminLayout";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft,
  Edit,
  Trash,
  LayoutPanelTop,
  ImageIcon,
  BookOpen,
  BookMarked,
  LibrarySquare,
  Users,
  Clock,
  Calendar,
  Check,
  X,
  Plus,
  Loader2,
  MoveVertical,
  ExternalLink,
  GripVertical,
  Eye,
  EyeOff,
  Search,
} from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { format } from "date-fns";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { Section, SectionType, SectionBanner } from "@/types";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import MediaPicker from "@/components/ui/media-picker";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";

// Helper function to get section type icon
const getSectionTypeIcon = (type: SectionType) => {
  switch (type) {
    case "banner":
      return <ImageIcon className="h-5 w-5" />;
    case "category":
      return <LibrarySquare className="h-5 w-5" />;
    case "book_summary":
      return <BookOpen className="h-5 w-5" />;
    case "collection":
      return <BookMarked className="h-5 w-5" />;
    case "people":
      return <Users className="h-5 w-5" />;
    default:
      return <LayoutPanelTop className="h-5 w-5" />;
  }
};

// Helper function to get section type icon with larger size
const getLargeTypeIcon = (type: SectionType) => {
  switch (type) {
    case "banner":
      return <ImageIcon className="h-14 w-14" />;
    case "category":
      return <LibrarySquare className="h-14 w-14" />;
    case "book_summary":
      return <BookOpen className="h-14 w-14" />;
    case "collection":
      return <BookMarked className="h-14 w-14" />;
    case "people":
      return <Users className="h-14 w-14" />;
    default:
      return <LayoutPanelTop className="h-14 w-14" />;
  }
};

// Helper function to get human-readable section type name
const getSectionTypeName = (type: SectionType) => {
  switch (type) {
    case "banner":
      return "Banners";
    case "category":
      return "Categories";
    case "book_summary":
      return "Books";
    case "collection":
      return "Collections";
    case "people":
      return "People";
    default:
      return type;
  }
};

// Helper function to get the badge color for a section type
const getSectionTypeColor = (type: SectionType) => {
  switch (type) {
    case "banner":
      return "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300";
    case "category":
      return "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300";
    case "book_summary":
      return "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300";
    case "collection":
      return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300";
    case "people":
      return "bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300";
    default:
      return "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300";
  }
};

// Banner dialog props interface
interface BannerDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (bannerData: Partial<SectionBanner>) => void;
  sectionId: string;
  banner?: SectionBanner;
  isLoading: boolean;
}

// Banner form data interface
interface BannerFormData {
  title: string;
  subtitle?: string;
  imageUrl?: string;
  linkUrl?: string;
  ctaText?: string;
  backgroundColor?: string;
  textColor?: string;
  isActive: boolean;
}

// Banner Dialog Component
function BannerDialog({
  isOpen,
  onClose,
  onSave,
  sectionId,
  banner,
  isLoading,
}: BannerDialogProps) {
  const isEditMode = !!banner;
  const [formData, setFormData] = useState<BannerFormData>({
    title: banner?.title || "",
    subtitle: banner?.subtitle || "",
    imageUrl: banner?.imageUrl || "",
    linkUrl: banner?.linkUrl || "",
    ctaText: banner?.ctaText || "",
    backgroundColor: banner?.backgroundColor || "",
    textColor: banner?.textColor || "",
    isActive: banner?.isActive ?? true,
  });

  // Reset form data when dialog opens or when banner changes
  useEffect(() => {
    // Initialize with default values for new banner or with existing banner data for edits
    setFormData({
      title: banner?.title || "",
      subtitle: banner?.subtitle || "",
      imageUrl: banner?.imageUrl || "",
      linkUrl: banner?.linkUrl || "",
      ctaText: banner?.ctaText || "",
      backgroundColor: banner?.backgroundColor || "",
      textColor: banner?.textColor || "",
      isActive: banner?.isActive ?? true,
    });
  }, [banner, isOpen]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      id: banner?.id,
      sectionId,
      ...formData,
      position: banner?.position,
    });
  };

  return (
    <DialogContent className="sm:max-w-[550px] max-h-[90vh] overflow-y-auto">
      <form onSubmit={handleSubmit}>
        <DialogHeader>
          <DialogTitle>
            {isEditMode ? "Edit Banner" : "Add New Banner"}
          </DialogTitle>
          <DialogDescription>
            {isEditMode
              ? "Update the banner details below"
              : "Fill out the form to add a new banner to this section"}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="title">
              Banner Title <span className="text-destructive">*</span>
            </Label>
            <Input
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="Enter banner title"
              required
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="subtitle">Subtitle</Label>
            <Input
              id="subtitle"
              name="subtitle"
              value={formData.subtitle}
              onChange={handleChange}
              placeholder="Enter banner subtitle (optional)"
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="imageUrl">Banner Image</Label>
            <MediaPicker
              onSelect={(url) =>
                setFormData((prev) => ({ ...prev, imageUrl: url }))
              }
              currentValue={formData.imageUrl || ""}
              title="Select Banner Image"
              description="Choose an image for this banner from your media library or upload a new one"
              buttonText="Select Banner Image"
              allowedTypes={[
                "image/jpeg",
                "image/jpg",
                "image/png",
                "image/webp",
              ]}
              showPreview={false}
            />
          </div>

          <div className="mt-4">
            <Label>Banner Preview</Label>
            <div
              className="mt-2 p-4 rounded-md border relative overflow-hidden h-[120px] flex flex-col justify-center w-full"
              style={{
                backgroundColor: formData.backgroundColor || "#f5f5f5",
                color: formData.textColor || "#000000",
              }}
            >
              {formData.imageUrl && (
                <div
                  className="absolute inset-0 bg-cover bg-center opacity-30 z-0"
                  style={{ backgroundImage: `url(${formData.imageUrl})` }}
                ></div>
              )}
              <div className="relative z-10 w-full">
                <h3
                  className="text-lg font-bold break-words leading-tight overflow-hidden"
                  style={{
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                  }}
                >
                  {formData.title || "Banner Title"}
                </h3>
                {formData.subtitle && (
                  <p
                    className="text-sm mt-1 break-words leading-tight overflow-hidden"
                    style={{
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                    }}
                  >
                    {formData.subtitle}
                  </p>
                )}
                {formData.ctaText && (
                  <Button
                    variant="secondary"
                    size="sm"
                    className="mt-2 truncate max-w-fit"
                    disabled
                  >
                    {formData.ctaText}
                  </Button>
                )}
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Preview with reduced image opacity
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="linkUrl">Link URL</Label>
              <Input
                id="linkUrl"
                name="linkUrl"
                value={formData.linkUrl}
                onChange={handleChange}
                placeholder="Enter destination URL"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="ctaText">CTA Text</Label>
              <Input
                id="ctaText"
                name="ctaText"
                value={formData.ctaText}
                onChange={handleChange}
                placeholder="e.g. Learn More"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="backgroundColor">Background Color</Label>
              <div className="flex gap-2 items-start flex-wrap">
                <div className="flex gap-2 items-center min-w-0 flex-1">
                  <div className="h-9 w-9 rounded border flex items-center justify-center overflow-hidden flex-shrink-0">
                    <input
                      type="color"
                      id="bgColor-picker"
                      value={formData.backgroundColor || "#f5f5f5"}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          backgroundColor: e.target.value,
                        }))
                      }
                      className="w-12 h-12 border-0 p-0 m-0 cursor-pointer"
                      style={{ transform: "scale(1.5)" }}
                    />
                  </div>
                  <Input
                    id="backgroundColor"
                    name="backgroundColor"
                    value={formData.backgroundColor}
                    onChange={handleChange}
                    placeholder="#f5f5f5"
                    className="font-mono min-w-0 flex-1"
                  />
                </div>
                {/* Quick colors */}
                <div className="flex flex-wrap gap-1 w-full mt-2">
                  {[
                    // Light mode backgrounds
                    "#E6F4FF",
                    "#D1E8FF",
                    "#EFF6FF",
                    "#EEF2FF",
                    "#E0FFF4",
                    "#FFFBEB",
                    // Dark mode backgrounds
                    "#0F172A",
                    "#1E3A8A",
                    "#1E293B",
                    "#312E81",
                    "#134E4A",
                    "#1F2937",
                  ].map((color) => (
                    <button
                      key={color}
                      className="w-5 h-5 rounded-full border hover:scale-110 transition-transform focus:outline-none focus:ring-1 focus:ring-primary flex-shrink-0"
                      style={{ backgroundColor: color }}
                      onClick={() =>
                        setFormData((prev) => ({
                          ...prev,
                          backgroundColor: color,
                        }))
                      }
                      title={color}
                      type="button"
                    />
                  ))}
                </div>
              </div>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="textColor">Text Color</Label>
              <div className="flex gap-2 items-start flex-wrap">
                <div className="flex gap-2 items-center min-w-0 flex-1">
                  <div className="h-9 w-9 rounded border flex items-center justify-center overflow-hidden flex-shrink-0">
                    <input
                      type="color"
                      id="textColor-picker"
                      value={formData.textColor || "#000000"}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          textColor: e.target.value,
                        }))
                      }
                      className="w-12 h-12 border-0 p-0 m-0 cursor-pointer"
                      style={{ transform: "scale(1.5)" }}
                    />
                  </div>
                  <Input
                    id="textColor"
                    name="textColor"
                    value={formData.textColor}
                    onChange={handleChange}
                    placeholder="#000000"
                    className="font-mono min-w-0 flex-1"
                  />
                </div>
                {/* Quick colors */}
                <div className="flex flex-wrap gap-1 w-full mt-2">
                  {[
                    // Light mode text colors
                    "#000000",
                    "#0A4B91",
                    "#1E3A8A",
                    "#2563EB",
                    "#4338CA",
                    "#0D766F",
                    // Dark mode text colors
                    "#FFFFFF",
                    "#93C5FD",
                    "#A5F3FC",
                    "#7DD3FC",
                    "#C7D2FE",
                    "#A7F3D0",
                  ].map((color) => (
                    <button
                      key={color}
                      className="w-5 h-5 rounded-full border hover:scale-110 transition-transform focus:outline-none focus:ring-1 focus:ring-primary flex-shrink-0"
                      style={{ backgroundColor: color }}
                      onClick={() =>
                        setFormData((prev) => ({ ...prev, textColor: color }))
                      }
                      title={color}
                      type="button"
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="isActive"
              checked={formData.isActive}
              onCheckedChange={(checked) =>
                setFormData((prev) => ({ ...prev, isActive: checked }))
              }
            />
            <Label htmlFor="isActive">
              {formData.isActive ? "Banner is active" : "Banner is hidden"}
            </Label>
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>{isEditMode ? "Update" : "Add"} Banner</>
            )}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

// Interface for item selector dialog props
interface ItemSelectorDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onAddItems: (itemIds: string[]) => void;
  sectionType: SectionType;
  sectionId: string;
  isLoading: boolean;
}

// Functions to fetch available items
const fetchAvailableCategories = async (
  search: string = "",
  existingItemIds: string[] = []
) => {
  try {
    let query = supabase.from("categories").select("id, name, is_active");

    if (search) {
      query = query.ilike("name", `%${search}%`);
    }

    // If there are existing item IDs, exclude them from the query
    if (existingItemIds.length > 0) {
      query = query.not("id", "in", `(${existingItemIds.join(",")})`);
    }

    const { data, error } = await query.order("name");

    if (error) throw error;

    return data.map((item) => ({
      id: item.id,
      title: item.name,
      isActive: item.is_active,
      type: "category" as SectionType,
    }));
  } catch (error) {
    console.error("Error fetching categories:", error);
    throw error;
  }
};

const fetchAvailableBooks = async (
  search: string = "",
  existingItemIds: string[] = []
) => {
  try {
    let query = supabase
      .from("books")
      .select("id, title, cover_image, is_active");

    if (search) {
      query = query.ilike("title", `%${search}%`);
    }

    // If there are existing item IDs, exclude them from the query
    if (existingItemIds.length > 0) {
      query = query.not("id", "in", `(${existingItemIds.join(",")})`);
    }

    const { data, error } = await query.order("title");

    if (error) throw error;

    return data.map((item) => ({
      id: item.id,
      title: item.title,
      coverImage: item.cover_image,
      isActive: item.is_active,
      type: "book_summary" as SectionType,
    }));
  } catch (error) {
    console.error("Error fetching books:", error);
    throw error;
  }
};

const fetchAvailableCollections = async (
  search: string = "",
  existingItemIds: string[] = []
) => {
  try {
    let query = supabase
      .from("collections")
      .select("id, title, subtitle, cover_image, is_active");

    if (search) {
      query = query.ilike("title", `%${search}%`);
    }

    // If there are existing item IDs, exclude them from the query
    if (existingItemIds.length > 0) {
      query = query.not("id", "in", `(${existingItemIds.join(",")})`);
    }

    const { data, error } = await query.order("title");

    if (error) throw error;

    return data.map((item) => ({
      id: item.id,
      title: item.title,
      subtitle: item.subtitle,
      coverImage: item.cover_image,
      isActive: item.is_active,
      type: "collection" as SectionType,
    }));
  } catch (error) {
    console.error("Error fetching collections:", error);
    throw error;
  }
};

const fetchAvailablePeople = async (
  search: string = "",
  existingItemIds: string[] = []
) => {
  try {
    let query = supabase.from("people").select("id, name, avatar_url");

    if (search) {
      query = query.ilike("name", `%${search}%`);
    }

    // If there are existing item IDs, exclude them from the query
    if (existingItemIds.length > 0) {
      query = query.not("id", "in", `(${existingItemIds.join(",")})`);
    }

    const { data, error } = await query.order("name");

    if (error) throw error;

    return data.map((item) => ({
      id: item.id,
      title: item.name,
      avatarUrl: item.avatar_url,
      type: "people" as SectionType,
    }));
  } catch (error) {
    console.error("Error fetching people:", error);
    throw error;
  }
};

// Item Selector Dialog Component
function ItemSelectorDialog({
  isOpen,
  onClose,
  onAddItems,
  sectionType,
  sectionId,
  isLoading,
}: ItemSelectorDialogProps) {
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const { toast } = useToast();

  // Get section data to get the IDs of existing items
  const { data: sectionData } = useQuery({
    queryKey: ["section", sectionId],
    enabled: isOpen,
  });

  // Extract IDs of items already in the section
  const existingItemIds = useMemo(() => {
    if (!sectionData?.items?.length) return [];
    return sectionData.items.map((item) => item.id);
  }, [sectionData]);

  // Fetch available items based on section type
  const {
    data: availableItems = [],
    isLoading: isLoadingItems,
    error: fetchError,
  } = useQuery({
    queryKey: [`available-${sectionType}`, searchQuery, existingItemIds],
    queryFn: async () => {
      switch (sectionType) {
        case "category":
          return fetchAvailableCategories(searchQuery, existingItemIds);
        case "book_summary":
          return fetchAvailableBooks(searchQuery, existingItemIds);
        case "collection":
          return fetchAvailableCollections(searchQuery, existingItemIds);
        case "people":
          return fetchAvailablePeople(searchQuery, existingItemIds);
        default:
          throw new Error(`Unsupported section type: ${sectionType}`);
      }
    },
    enabled: isOpen, // Only fetch when dialog is open
  });

  const handleSelect = (itemId: string) => {
    if (selectedItems.includes(itemId)) {
      setSelectedItems(selectedItems.filter((id) => id !== itemId));
    } else {
      setSelectedItems([...selectedItems, itemId]);
    }
  };

  const handleSelectAll = () => {
    if (selectedItems.length === availableItems.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(availableItems.map((item) => item.id));
    }
  };

  const handleSubmit = () => {
    if (selectedItems.length === 0) {
      toast({
        title: "No items selected",
        description: "Please select at least one item to add.",
        variant: "destructive",
      });
      return;
    }
    onAddItems(selectedItems);
  };

  // Reset selections when dialog opens/closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedItems([]);
      setSearchQuery("");
    }
  }, [isOpen]);

  // Get message for empty state
  const getEmptyStateMessage = () => {
    if (searchQuery) {
      return `No ${getSectionTypeName(
        sectionType
      ).toLowerCase()} found matching "${searchQuery}".`;
    }

    // Check if all items are already added
    const hasExistingItems = sectionData?.items?.length > 0;

    return `No ${getSectionTypeName(
      sectionType
    ).toLowerCase()} available to add to this section. ${
      hasExistingItems ? "All available items have already been added." : ""
    }`;
  };

  return (
    <DialogContent className="sm:max-w-[700px]">
      <DialogHeader>
        <DialogTitle>
          Add {getSectionTypeName(sectionType)} to Section
        </DialogTitle>
        <DialogDescription>
          Select items to add to this section.
        </DialogDescription>
      </DialogHeader>

      <div className="py-4">
        <div className="flex items-center space-x-2 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={`Search ${getSectionTypeName(
                sectionType
              ).toLowerCase()}...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8"
            />
          </div>
        </div>

        <div className="border rounded-md h-[300px] overflow-y-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[40px]">
                  {availableItems.length > 0 && (
                    <input
                      type="checkbox"
                      checked={
                        selectedItems.length === availableItems.length &&
                        availableItems.length > 0
                      }
                      onChange={handleSelectAll}
                      className="h-4 w-4"
                    />
                  )}
                </TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Preview</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoadingItems ? (
                <TableRow>
                  <TableCell colSpan={3} className="h-[200px] text-center">
                    <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                  </TableCell>
                </TableRow>
              ) : fetchError ? (
                <TableRow>
                  <TableCell colSpan={3} className="h-[200px] text-center">
                    <p className="text-muted-foreground">
                      Error loading items. Please try again.
                    </p>
                  </TableCell>
                </TableRow>
              ) : availableItems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} className="h-[200px] text-center">
                    <p className="text-muted-foreground">
                      {getEmptyStateMessage()}
                    </p>
                  </TableCell>
                </TableRow>
              ) : (
                availableItems.map((item) => (
                  <TableRow
                    key={item.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleSelect(item.id)}
                  >
                    <TableCell>
                      <input
                        type="checkbox"
                        checked={selectedItems.includes(item.id)}
                        onChange={() => handleSelect(item.id)}
                        className="h-4 w-4"
                      />
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">{item.title}</div>
                      {"subtitle" in item && item.subtitle && (
                        <div className="text-sm text-muted-foreground">
                          {item.subtitle}
                        </div>
                      )}
                      {"isActive" in item && item.isActive === false && (
                        <Badge
                          variant="outline"
                          className="bg-gray-100 text-gray-600 dark:bg-gray-800/50 dark:text-gray-400 h-5 px-1 mt-1"
                        >
                          <EyeOff className="h-3 w-3 mr-1" />
                          Unpublished
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {("coverImage" in item || "avatarUrl" in item) && (
                        <div className="h-9 w-9 rounded-md overflow-hidden bg-muted flex items-center justify-center">
                          <img
                            src={
                              "coverImage" in item
                                ? item.coverImage
                                : "avatarUrl" in item
                                ? item.avatarUrl
                                : undefined
                            }
                            alt={item.title}
                            className="h-full w-full object-cover"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src =
                                "https://placehold.co/36x36/gray/white?text=NA";
                            }}
                          />
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        <div className="mt-4 text-sm text-muted-foreground">
          {selectedItems.length > 0
            ? `${selectedItems.length} ${
                selectedItems.length === 1
                  ? getSectionTypeName(sectionType).slice(0, -1).toLowerCase()
                  : getSectionTypeName(sectionType).toLowerCase()
              } selected`
            : `No ${getSectionTypeName(sectionType).toLowerCase()} selected`}
        </div>
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={isLoading || selectedItems.length === 0}
        >
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Adding...
            </>
          ) : (
            <>
              Add {selectedItems.length}{" "}
              {selectedItems.length === 1
                ? getSectionTypeName(sectionType).slice(0, -1)
                : getSectionTypeName(sectionType)}
            </>
          )}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

// Generic interface for any type of content item
interface ContentItem {
  id: string;
  position: number;
  title?: string;
  name?: string;
  subtitle?: string;
  coverImage?: string;
  avatarUrl?: string;
  imageUrl?: string;
  isActive?: boolean;
}

// Interface for sortable content item props
interface SortableContentItemProps {
  item: ContentItem | SectionBanner;
  type: SectionType;
  onRemove: (id: string) => void;
  onToggleVisibility?: (id: string, isActive: boolean) => void;
  onEdit?: (id: string) => void;
}

// Sortable Content Item component for DnD
function SortableContentItem({
  item,
  type,
  onRemove,
  onToggleVisibility,
  onEdit,
}: SortableContentItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: item.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : 1,
  };

  // Display title based on item type
  const getItemTitle = () => {
    if ("title" in item) return item.title;
    if ("name" in item) return item.name;
    return `Item ${item.position}`;
  };

  // Get image url based on item type
  const getItemImage = () => {
    if ("imageUrl" in item) return item.imageUrl;
    if ("coverImage" in item) return item.coverImage;
    if ("avatarUrl" in item) return item.avatarUrl;
    return null;
  };

  const isActive = "isActive" in item ? item.isActive : true;

  return (
    <TableRow
      ref={setNodeRef}
      style={style}
      className={cn(
        "hover:bg-muted/40",
        isDragging ? "opacity-50 bg-accent" : "",
        !isActive ? "opacity-60" : ""
      )}
    >
      <TableCell>
        <div className="flex items-center space-x-4">
          <div
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing p-1 mr-2"
            data-drag-handle
          >
            <GripVertical className="h-5 w-5 text-muted-foreground" />
          </div>
          <div className="flex items-center gap-3">
            {type === "banner" && "imageUrl" in item ? (
              <HoverCard>
                <HoverCardTrigger asChild>
                  <div className="h-10 w-10 rounded-md overflow-hidden bg-muted flex items-center justify-center cursor-pointer">
                    <img
                      src={item.imageUrl || ""}
                      alt={getItemTitle()}
                      className="h-full w-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src =
                          "https://placehold.co/40x40/gray/white?text=NA";
                      }}
                    />
                  </div>
                </HoverCardTrigger>
                <HoverCardContent className="w-64 p-0">
                  <div
                    className="p-3 relative overflow-hidden"
                    style={{
                      backgroundColor:
                        "backgroundColor" in item
                          ? item.backgroundColor || "#f8f9fa"
                          : "#f8f9fa",
                      color:
                        "textColor" in item
                          ? item.textColor || "#000000"
                          : "#000000",
                    }}
                  >
                    {item.imageUrl && (
                      <div
                        className="absolute inset-0 bg-cover bg-center opacity-30 z-0"
                        style={{ backgroundImage: `url(${item.imageUrl})` }}
                      ></div>
                    )}
                    <div className="relative z-10">
                      <h3 className="text-md font-bold truncate">
                        {item.title}
                      </h3>
                      {item.subtitle && (
                        <p className="text-xs mt-1 truncate">{item.subtitle}</p>
                      )}
                      {"ctaText" in item && item.ctaText && (
                        <Button
                          variant="secondary"
                          size="sm"
                          className="mt-2 text-xs h-7 px-2"
                          disabled
                        >
                          {item.ctaText}
                        </Button>
                      )}
                      {"linkUrl" in item && item.linkUrl && (
                        <p className="text-xs mt-1 opacity-70 truncate">
                          {item.linkUrl}
                        </p>
                      )}
                    </div>
                  </div>
                </HoverCardContent>
              </HoverCard>
            ) : (
              getItemImage() && (
                <div className="h-10 w-10 rounded-md overflow-hidden bg-muted flex items-center justify-center">
                  <img
                    src={getItemImage()!}
                    alt={getItemTitle()}
                    className={cn(
                      "h-full w-full object-cover",
                      type === "banner" && "bg-cover"
                    )}
                    onError={(e) => {
                      (e.target as HTMLImageElement).src =
                        "https://placehold.co/40x40/gray/white?text=NA";
                    }}
                  />
                </div>
              )
            )}
            <div>
              <div className="font-medium">{getItemTitle()}</div>
              {"subtitle" in item && item.subtitle && (
                <div className="text-sm text-muted-foreground">
                  {item.subtitle}
                </div>
              )}
              {!isActive && (
                <Badge
                  variant="outline"
                  className="bg-gray-100 text-gray-600 dark:bg-gray-800/50 dark:text-gray-400 h-5 px-1 mt-1"
                >
                  <EyeOff className="h-3 w-3 mr-1" />
                  Unpublished
                </Badge>
              )}
              {type === "banner" &&
                "backgroundColor" in item &&
                item.backgroundColor && (
                  <div className="flex items-center gap-1 mt-1">
                    <div
                      className="h-3 w-3 rounded-full border"
                      style={{ backgroundColor: item.backgroundColor }}
                      title="Background color"
                    ></div>
                    {item.textColor && (
                      <div
                        className="h-3 w-3 rounded-full border ml-1"
                        style={{ backgroundColor: item.textColor }}
                        title="Text color"
                      ></div>
                    )}
                  </div>
                )}
            </div>
          </div>
        </div>
      </TableCell>
      <TableCell>
        <Badge variant="outline" className="bg-background">
          {item.position}
        </Badge>
      </TableCell>
      <TableCell className="text-right">
        <div className="flex items-center justify-end gap-2">
          {onEdit && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onEdit(item.id)}
              className="h-8 w-8"
            >
              <Edit className="h-4 w-4" />
            </Button>
          )}
          {onToggleVisibility && "isActive" in item && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onToggleVisibility(item.id, !item.isActive)}
              className="h-8 w-8"
              title={isActive ? "Hide item" : "Show item"}
            >
              {isActive ? (
                <Eye className="h-4 w-4" />
              ) : (
                <EyeOff className="h-4 w-4" />
              )}
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onRemove(item.id)}
            className="h-8 w-8 text-destructive hover:text-destructive"
          >
            <Trash className="h-4 w-4" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

const SectionDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("details");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [removeItemId, setRemoveItemId] = useState<string | null>(null);
  const [removeItemDialogOpen, setRemoveItemDialogOpen] = useState(false);

  // Dialog states
  const [bannerDialogOpen, setBannerDialogOpen] = useState(false);
  const [itemSelectorDialogOpen, setItemSelectorDialogOpen] = useState(false);
  const [editingBanner, setEditingBanner] = useState<SectionBanner | undefined>(
    undefined
  );

  // Setup sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Fetch section data
  const { data: section, isLoading } = useQuery({
    queryKey: ["section", id],
    queryFn: () => fetchSectionById(id!),
    enabled: !!id,
  });

  // Delete section mutation
  const deleteMutation = useMutation({
    mutationFn: deleteSection,
    onSuccess: () => {
      navigate("/admin/sections");
      toast({
        title: "Section deleted",
        description: "The section has been permanently deleted.",
      });
    },
  });

  // Add banner mutation
  const addBannerMutation = useMutation({
    mutationFn: async (bannerData: Partial<SectionBanner>) => {
      if (bannerData.id) {
        // Update existing banner
        return updateBanner(bannerData.id, bannerData);
      } else {
        // Create new banner
        return addBannerToSection(
          id!,
          bannerData as Omit<
            SectionBanner,
            "id" | "sectionId" | "createdAt" | "updatedAt"
          >
        );
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["section", id] });
      toast({
        title: editingBanner ? "Banner updated" : "Banner added",
        description: `The banner has been ${
          editingBanner ? "updated" : "added"
        } successfully.`,
      });
      setBannerDialogOpen(false);
      setEditingBanner(undefined);
    },
    onError: (error) => {
      console.error(
        `Error ${editingBanner ? "updating" : "adding"} banner:`,
        error
      );
      toast({
        title: `Error ${editingBanner ? "updating" : "adding"} banner`,
        description:
          "There was a problem with the operation. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Add items mutation
  const addItemsMutation = useMutation({
    mutationFn: async (itemIds: string[]) => {
      if (!section) throw new Error("Section not found");

      // Calculate the highest current position
      const highestPosition = section.items?.length
        ? Math.max(...section.items.map((item) => item.position))
        : 0;

      // Add each item sequentially with increasing positions
      const addPromises = itemIds.map((itemId, index) => {
        return addItemToSection(
          id!,
          itemId,
          section.type,
          highestPosition + index + 1
        );
      });

      return Promise.all(addPromises);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["section", id] });
      toast({
        title: "Items added",
        description: "The items have been added to this section.",
      });
      setItemSelectorDialogOpen(false);
    },
    onError: (error) => {
      console.error("Error adding items:", error);
      toast({
        title: "Error adding items",
        description: "There was a problem adding the items. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Remove item from section mutation
  const removeItemMutation = useMutation({
    mutationFn: async ({
      itemId,
      type,
    }: {
      itemId: string;
      type: SectionType;
    }) => {
      return removeItemFromSection(id!, itemId, type);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["section", id] });
      toast({
        title: "Item removed",
        description: "The item has been removed from this section.",
      });
    },
    onError: (error) => {
      console.error("Error removing item:", error);
      toast({
        title: "Error",
        description: "Failed to remove item. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Remove banner mutation
  const removeBannerMutation = useMutation({
    mutationFn: deleteBanner,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["section", id] });
      toast({
        title: "Banner removed",
        description: "The banner has been removed from this section.",
      });
    },
    onError: (error) => {
      console.error("Error removing banner:", error);
      toast({
        title: "Error",
        description: "Failed to remove banner. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Update item positions mutation
  const updatePositionsMutation = useMutation({
    mutationFn: async ({
      itemPositions,
      type,
    }: {
      itemPositions: { itemId: string; position: number }[];
      type: SectionType;
    }) => {
      return updateSectionItemPositions(id!, itemPositions, type);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["section", id] });
      toast({
        title: "Positions updated",
        description: "Item positions have been updated.",
      });
    },
    onError: (error) => {
      console.error("Error updating positions:", error);
      toast({
        title: "Error",
        description: "Failed to update positions. Please try again.",
        variant: "destructive",
      });
    },
  });

  // Handle section deletion
  const handleDelete = async () => {
    try {
      await deleteMutation.mutateAsync(id!);
    } catch (error) {
      console.error("Failed to delete section:", error);
      toast({
        title: "Error",
        description: "Failed to delete section. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Handle banner edit
  const handleEditBanner = (bannerId: string) => {
    if (section && section.banners) {
      const bannerToEdit = section.banners.find(
        (banner) => banner.id === bannerId
      );
      if (bannerToEdit) {
        setEditingBanner(bannerToEdit);
        setBannerDialogOpen(true);
      } else {
        toast({
          title: "Error",
          description: "Banner not found. Please try again.",
          variant: "destructive",
        });
      }
    }
  };

  // Handle banner save
  const handleSaveBanner = (bannerData: Partial<SectionBanner>) => {
    // When updating existing banner, just proceed with the update
    if (bannerData.id) {
      addBannerMutation.mutate(bannerData);
      return;
    }

    // When adding a new banner, check for duplicates first
    if (section?.banners?.length) {
      const isDuplicate = section.banners.some(
        (existingBanner) =>
          existingBanner.title.toLowerCase() === bannerData.title?.toLowerCase()
      );

      if (isDuplicate) {
        toast({
          title: "Duplicate banner",
          description: `A banner with the title "${bannerData.title}" already exists in this section.`,
          variant: "destructive",
        });
        return;
      }
    }

    // Proceed with adding the new banner
    addBannerMutation.mutate(bannerData);
  };

  // Handle item removal
  const handleRemoveItem = (itemId: string) => {
    setRemoveItemId(itemId);
    setRemoveItemDialogOpen(true);
  };

  // Handle adding items
  const handleAddItems = (itemIds: string[]) => {
    addItemsMutation.mutate(itemIds);
  };

  // Confirm item removal
  const confirmRemoveItem = async () => {
    if (!removeItemId || !section) return;

    try {
      // Different handling based on section type
      if (section.type === "banner") {
        await removeBannerMutation.mutateAsync(removeItemId);
      } else {
        await removeItemMutation.mutateAsync({
          itemId: removeItemId,
          type: section.type,
        });
      }
    } catch (error) {
      console.error("Error removing item:", error);
    } finally {
      setRemoveItemDialogOpen(false);
      setRemoveItemId(null);
    }
  };

  // Handle drag end for reordering
  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id || !section) {
      return;
    }

    // Handle banner reordering
    if (section.type === "banner" && section.banners) {
      const oldIndex = section.banners.findIndex(
        (banner) => banner.id === active.id
      );
      const newIndex = section.banners.findIndex(
        (banner) => banner.id === over.id
      );

      if (oldIndex !== -1 && newIndex !== -1) {
        // Create a new array with the moved item
        const newBanners = arrayMove(section.banners, oldIndex, newIndex);

        // Update positions based on array index with sequential values
        const bannersWithNewPositions = newBanners.map((banner, idx) => ({
          ...banner,
          position: idx + 1,
        }));

        // Optimistically update UI
        queryClient.setQueryData(["section", id], {
          ...section,
          banners: bannersWithNewPositions,
        });

        // Save the banner positions using the updatePositionsMutation
        try {
          // Convert banners to the format expected by the API
          const itemPositions = bannersWithNewPositions.map((banner) => ({
            itemId: banner.id,
            position: banner.position,
          }));

          // Call API to update positions
          await updatePositionsMutation.mutateAsync({
            itemPositions,
            type: "banner",
          });
        } catch (error) {
          console.error("Error updating banner positions:", error);
          // Revert on error by refreshing the data
          queryClient.invalidateQueries({ queryKey: ["section", id] });
          toast({
            title: "Error updating positions",
            description: "Failed to update banner positions. Please try again.",
            variant: "destructive",
          });
        }
      }
    }
    // Handle other content types
    else if (section.items) {
      const oldIndex = section.items.findIndex((item) => item.id === active.id);
      const newIndex = section.items.findIndex((item) => item.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        // Create a new array with the moved item
        const newItems = arrayMove(section.items, oldIndex, newIndex);

        // Update positions based on array index with sequential values
        const itemsWithNewPositions = newItems.map((item, idx) => ({
          ...item,
          position: idx + 1,
        }));

        // Optimistically update UI
        queryClient.setQueryData(["section", id], {
          ...section,
          items: itemsWithNewPositions,
        });

        // Call API to update positions
        const itemPositions = itemsWithNewPositions.map((item) => ({
          itemId: item.id,
          position: item.position,
        }));

        await updatePositionsMutation.mutateAsync({
          itemPositions,
          type: section.type,
        });
      }
    }
  };

  if (isLoading) {
    return (
      <AdminLayout title="Section Details">
        <div className="flex items-center justify-center h-full">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      </AdminLayout>
    );
  }

  if (!section) {
    return (
      <AdminLayout title="Section Details">
        <div className="flex flex-col items-center justify-center h-full gap-4">
          <h1 className="text-2xl font-bold">Section not found</h1>
          <p className="text-muted-foreground">
            The section you're looking for doesn't exist or has been removed.
          </p>
          <Button onClick={() => navigate("/admin/sections")}>
            Go back to Sections
          </Button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Section Details">
      {/* Header with back button, title and actions */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">{section.title}</h1>
            {section.subtitle && (
              <p className="text-sm text-muted-foreground">
                {section.subtitle}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="default"
            onClick={() => navigate(`/admin/sections/${id}/edit`)}
          >
            <Edit className="mr-2 h-4 w-4" /> Edit Section
          </Button>

          <AlertDialog
            open={deleteDialogOpen}
            onOpenChange={setDeleteDialogOpen}
          >
            <AlertDialogTrigger asChild>
              <Button variant="destructive">
                <Trash className="mr-2 h-4 w-4" />
                Delete
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently delete the section "{section.title}".
                  This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleDelete}>
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        <div className="md:col-span-2 space-y-6">
          {/* Section Info Card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle>Section Details</CardTitle>
              {section.subtitle && (
                <CardDescription>{section.subtitle}</CardDescription>
              )}
            </CardHeader>
            <CardContent className="grid gap-6 md:grid-cols-2">
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">
                  Title
                </p>
                <p className="text-lg font-medium">{section.title}</p>
              </div>

              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">
                  Content Items
                </p>
                <p className="flex items-center gap-1">
                  {getSectionTypeIcon(section.type)}
                  <span className="text-lg font-medium ml-1">
                    {section.type === "banner"
                      ? `${section.banners?.length || 0} banner${
                          section.banners?.length !== 1 ? "s" : ""
                        }`
                      : `${section.items?.length || 0} item${
                          section.items?.length !== 1 ? "s" : ""
                        }`}
                  </span>
                </p>
              </div>

              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">
                  Type
                </p>
                <div className="flex items-center gap-2">
                  <Badge
                    variant="outline"
                    className={cn(
                      "px-2 py-1",
                      getSectionTypeColor(section.type)
                    )}
                  >
                    <span className="mr-1.5">
                      {getSectionTypeIcon(section.type)}
                    </span>
                    {getSectionTypeName(section.type)}
                  </Badge>
                </div>
              </div>

              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">
                  Position
                </p>
                <p className="text-lg font-medium">{section.position}</p>
              </div>

              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">
                  Created
                </p>
                <p className="text-base flex items-center gap-1">
                  <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                  {format(new Date(section.createdAt), "MMM d, yyyy")}
                </p>
              </div>

              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">
                  Last Updated
                </p>
                <p className="text-base flex items-center gap-1">
                  <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                  {format(new Date(section.updatedAt), "MMM d, yyyy")}
                </p>
              </div>

              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">
                  Display Style
                </p>
                <p className="text-lg font-medium">
                  {section.displayStyle || "Default"}
                </p>
              </div>

              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">
                  Status
                </p>
                <Badge
                  variant={section.isActive ? "default" : "outline"}
                  className={cn(
                    "px-2 py-1",
                    section.isActive
                      ? "bg-green-100 text-green-800 hover:bg-green-200 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:hover:bg-green-900/40"
                      : "bg-gray-100 text-gray-800 hover:bg-gray-200 dark:bg-gray-800/50 dark:text-gray-300 dark:hover:bg-gray-800/70"
                  )}
                >
                  {section.isActive ? (
                    <Check className="mr-1 h-3 w-3 inline" />
                  ) : (
                    <X className="mr-1 h-3 w-3 inline" />
                  )}
                  {section.isActive ? "Active" : "Inactive"}
                </Badge>
              </div>

              {section.description && (
                <div className="md:col-span-2">
                  <p className="text-sm font-medium text-muted-foreground mb-1">
                    Description
                  </p>
                  <p className="text-base whitespace-pre-line p-3 bg-secondary/30 rounded-md">
                    {section.description}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Section Content Card */}
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Section Content</CardTitle>
                  <CardDescription>
                    {section.type === "banner"
                      ? `${section.banners?.length || 0} banner${
                          section.banners?.length !== 1 ? "s" : ""
                        } in this section`
                      : `${section.items?.length || 0} item${
                          section.items?.length !== 1 ? "s" : ""
                        } in this section`}
                  </CardDescription>
                </div>
                <Button
                  className="gap-2"
                  onClick={() => {
                    if (section.type === "banner") {
                      setBannerDialogOpen(true);
                      setEditingBanner(undefined);
                    } else {
                      setItemSelectorDialogOpen(true);
                    }
                  }}
                >
                  <Plus className="h-4 w-4" />
                  Add {section.type === "banner" ? "Banner" : "Item"}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {(
                section.type === "banner"
                  ? section.banners?.length === 0
                  : section.items?.length === 0
              ) ? (
                <div className="text-center py-8">
                  <LayoutPanelTop className="h-12 w-12 mx-auto text-muted-foreground opacity-50 mb-3" />
                  <h3 className="text-lg font-medium mb-1">
                    No content in this section
                  </h3>
                  <p className="text-muted-foreground">
                    This section doesn't contain any content items yet.
                  </p>
                </div>
              ) : (
                <div className="border rounded-md overflow-hidden">
                  <Table>
                    <TableHeader className="bg-muted/50">
                      <TableRow>
                        <TableHead>Item</TableHead>
                        <TableHead>Position</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {section.type === "banner" && section.banners?.length ? (
                        <DndContext
                          sensors={sensors}
                          collisionDetection={closestCenter}
                          onDragEnd={handleDragEnd}
                        >
                          <SortableContext
                            items={section.banners.map((banner) => banner.id)}
                            strategy={verticalListSortingStrategy}
                          >
                            {section.banners.map((banner) => (
                              <SortableContentItem
                                key={banner.id}
                                item={banner}
                                type="banner"
                                onRemove={handleRemoveItem}
                                onEdit={handleEditBanner}
                                onToggleVisibility={(bannerId, isActive) => {
                                  const banner = section.banners?.find(
                                    (b) => b.id === bannerId
                                  );
                                  if (banner) {
                                    addBannerMutation.mutate({
                                      id: bannerId,
                                      isActive,
                                    });
                                  }
                                }}
                              />
                            ))}
                          </SortableContext>
                        </DndContext>
                      ) : section.items?.length ? (
                        <DndContext
                          sensors={sensors}
                          collisionDetection={closestCenter}
                          onDragEnd={handleDragEnd}
                        >
                          <SortableContext
                            items={section.items.map((item) => item.id)}
                            strategy={verticalListSortingStrategy}
                          >
                            {section.items.map((item) => (
                              <SortableContentItem
                                key={item.id}
                                item={item}
                                type={section.type}
                                onRemove={handleRemoveItem}
                              />
                            ))}
                          </SortableContext>
                        </DndContext>
                      ) : (
                        <TableRow>
                          <TableCell colSpan={3} className="h-24 text-center">
                            <Loader2 className="h-6 w-6 animate-spin text-primary mx-auto" />
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          {/* Section Icon Card */}
          <Card>
            <CardHeader>
              <CardTitle>Section Type</CardTitle>
            </CardHeader>
            <CardContent className="flex justify-center py-6">
              <div
                className={cn(
                  "h-24 w-24 rounded-full flex items-center justify-center",
                  getSectionTypeColor(section.type)
                )}
              >
                <div className="h-16 w-16 flex items-center justify-center">
                  {getLargeTypeIcon(section.type)}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Section Stats Card */}
          <Card>
            <CardHeader>
              <CardTitle>Section Stats</CardTitle>
              <CardDescription>Quick information</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  Content Items
                </span>
                <Badge variant="outline" className="bg-background">
                  {section.type === "banner"
                    ? section.banners?.length || 0
                    : section.items?.length || 0}
                </Badge>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Type</span>
                <span className="font-medium">
                  {getSectionTypeName(section.type)}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Status</span>
                <div className="flex items-center gap-1">
                  {section.isActive ? (
                    <Check className="h-4 w-4 text-green-500" />
                  ) : (
                    <X className="h-4 w-4 text-gray-500" />
                  )}
                  <span className="font-medium">
                    {section.isActive ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Position</span>
                <div className="flex items-center gap-1">
                  <span className="font-medium">{section.position}</span>
                </div>
              </div>

              <div className="pt-2 mt-2 border-t border-border text-xs text-center text-muted-foreground">
                You can modify all section properties using the Edit Section
                button.
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Remove Item Confirmation Dialog */}
      <AlertDialog
        open={removeItemDialogOpen}
        onOpenChange={setRemoveItemDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove item from section?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the item from this section. The item itself will
              not be deleted from the system.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setRemoveItemId(null)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmRemoveItem}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Banner Dialog */}
      <Dialog open={bannerDialogOpen} onOpenChange={setBannerDialogOpen}>
        <BannerDialog
          isOpen={bannerDialogOpen}
          onClose={() => setBannerDialogOpen(false)}
          onSave={handleSaveBanner}
          sectionId={id!}
          banner={editingBanner}
          isLoading={addBannerMutation.isPending}
        />
      </Dialog>

      {/* Item Selector Dialog */}
      <Dialog
        open={itemSelectorDialogOpen}
        onOpenChange={setItemSelectorDialogOpen}
      >
        {section && (
          <ItemSelectorDialog
            isOpen={itemSelectorDialogOpen}
            onClose={() => setItemSelectorDialogOpen(false)}
            onAddItems={handleAddItems}
            sectionType={section.type}
            sectionId={id!}
            isLoading={addItemsMutation.isPending}
          />
        )}
      </Dialog>
    </AdminLayout>
  );
};

export default SectionDetails;
